import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { coordSystems } from "shared/common/utils/constants/coordSystems";
import { addThousandsSeparator } from "shared/common/utils/general-utils";
import MeasureResultField from "./MeasureResultField";

interface PositionAttribute {
  id: string;
  label: string;
  value: string;
}

export default function CoordinateContent() {
  const viewer = defaultStore((state) => state.viewer);

  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );
  const coordinateToolValues = sidebarStore(
    (state) => state.coordinateToolValues
  );
  const coordinatesActiveAttributes = sidebarStore(
    (state) => state.coordinatesActiveAttributes
  );
  const changeCoordinatesActiveAttributes = sidebarStore(
    (state) => state.changeCoordinatesActiveAttributes
  );

  const { t } = useTranslation();

  const [epsg, setEpsg] = useState<string>("");
  const [epsgForHeight, setEpsgForHeight] = useState<string>("");
  const defaultPositionValue = "";
  const defaultHeightValue = "0 ";

  const [position, setPosition] = useState<string>(defaultPositionValue);
  const [precPosition, setPrecPosition] =
    useState<string>(defaultPositionValue);
  const [height, setHeight] = useState<string>(defaultHeightValue);
  const [precHeight, setPrecHeight] = useState<string>(defaultHeightValue);
  const [attributes, setAttributes] = useState<PositionAttribute[]>([]);

  //add epsg code and name if viewer is defined
  useEffect(() => {
    if (viewer !== undefined) {
      const geoframe = viewer.getScene().getCurrentGeoframe();
      const epsgCode: number = geoframe.srid;
      const epsgForHeightCode: number = geoframe.sridHeight;
      setEpsg(coordSystems[epsgCode]);
      setEpsgForHeight(coordSystems[epsgForHeightCode]);
    }
  }, [viewer]);

  useEffect(() => {
    setAttributes([
      {
        id: "position",
        label: `${t("coordinate.position")} | ${epsg}`,
        value: position,
      },
      {
        id: "height",
        label:
          epsgForHeight === ""
            ? t("coordinate.height")
            : `${t("coordinate.height")} | ${epsgForHeight}`,
        value: `${height} m`,
      },
    ]);
  }, [epsg, epsgForHeight, position, height]);

  //format and return in the right way coordinates
  useEffect(() => {
    if (coordinateToolValues !== undefined) {
      const easting =
        coordinateToolValues.pointfeature.geometry.coordinates[0].toFixed(2);
      const northing =
        coordinateToolValues.pointfeature.geometry.coordinates[1].toFixed(2);
      const height =
        coordinateToolValues.pointfeature.geometry.coordinates[2].toFixed(2);

      setPosition(
        addThousandsSeparator(easting) + ", " + addThousandsSeparator(northing)
      );
      setHeight(height);

      const prec_east = Math.pow(
        coordinateToolValues.measurepoint.precGlobalEasting,
        2
      );
      const prec_north = Math.pow(
        coordinateToolValues.measurepoint.precGlobalNorthing,
        2
      );

      // var prec_position: string = Math.sqrt(
      //   Math.pow(coordinateToolValues.measurepoint.precGlobalEasting, 2) +
      //     Math.pow(coordinateToolValues.measurepoint.precGlobalNorthing, 2)
      // );
      const prec_height = coordinateToolValues.measurepoint.precGlobalHeight;
      if (isNaN(prec_east) || isNaN(prec_north)) {
        setPrecPosition("-");
      } else {
        setPrecPosition(prec_east + ", " + prec_north);
      }
      if (isNaN(prec_height)) {
        setPrecHeight("-");
      } else {
        setPrecHeight(prec_height);
      }
    } else {
      setPosition(defaultPositionValue);
      setHeight(defaultHeightValue);
      setPrecPosition(defaultPositionValue);
      setPrecHeight(defaultHeightValue);
    }
  }, [coordinateToolValues]);

  return (
    <Box width="100%" display={"flex"} flexDirection={"column"} gap={0.5}>
      {attributes
        .filter((item: PositionAttribute) => {
          if (showSmallToolContent) {
            return coordinatesActiveAttributes.includes(item.id);
          } else {
            return item;
          }
        })
        .map((item: PositionAttribute) => (
          <MeasureResultField
            key={item.id}
            id={item.id}
            title={item.label}
            value={item.value}
            loggingString={item.id}
            activeAttributes={coordinatesActiveAttributes}
            changeActiveAttributes={changeCoordinatesActiveAttributes}
            showPin={attributes.length > 1}
          />
        ))}
    </Box>
  );
}
